import React, { useEffect, useState } from 'react'
import DataPreview from './DataPreview'
import DataViewer from './DataViewer'
import { useTranslation } from 'react-i18next'
import { extractUploadClasses } from '../../../utils/classNameProcessor'

function StudentAnalysis({ studentsData, studentAnalysis, currentClasses = [], settings }) {
    const { t, locale } = useTranslation()
    const [activeTab, setActiveTab] = useState('summary')
    const [expandedSections, setExpandedSections] = useState({
        issues: false,
        warnings: false,
        missingClass: false,
        duplicateEmails: false,
        duplicateIds: false,
        missingNames: false,
        missingIds: false,
        invalidGender: false,
        validRecords: false,
        classChanges: false,
        primaryStudents: false,
        secondaryStudents: false,
        missingPasswords: false,
        missingEmails: false,
    })
    const [classComparison, setClassComparison] = useState({
        added: [],
        removed: [],
        renamed: [],
        unchanged: [],
    })

    // Extract classes from student data and compare with current classes
    useEffect(() => {
        if (studentsData && studentsData.length > 0 && currentClasses.length > 0) {
            compareClasses()
        }
    }, [studentsData, currentClasses])

    if (!studentsData || studentsData.length === 0 || !studentAnalysis) {
        return null
    }

    // Classes are identified by (name, year): the same name can legitimately exist in several years
    const classKey = (name, year) => `${String(name ?? '').trim().toLowerCase()}|${String(year ?? '').trim()}`

    // Compare the classes found in the upload with the classes that already exist on the server
    const compareClasses = () => {
        const uploadClasses = extractUploadClasses(studentsData)
        const currentClassesMap = new Map(currentClasses.map((c) => [classKey(c.name, c.year), c]))
        const uploadClassesMap = new Map(uploadClasses.map((c) => [classKey(c.name, c.year), c]))

        const unchanged = uploadClasses.filter((c) => currentClassesMap.has(classKey(c.name, c.year)))
        const notExisting = uploadClasses.filter((c) => !currentClassesMap.has(classKey(c.name, c.year)))

        // An uploaded class whose raw sheet value matches an existing class of the same year is a rename
        // (e.g. existing "1" in YR-1 becomes "YR-1-1"), not an add + remove
        const renamed = notExisting
            .filter((c) => c.rawName && c.rawName !== c.name && currentClassesMap.has(classKey(c.rawName, c.year)))
            .map((c) => ({ ...c, previousName: currentClassesMap.get(classKey(c.rawName, c.year)).name }))
        const renamedFromKeys = new Set(renamed.map((c) => classKey(c.rawName, c.year)))

        const added = notExisting.filter((c) => !renamedFromKeys.has(classKey(c.rawName, c.year)) || c.rawName === c.name)
        const removed = currentClasses.filter((c) => !uploadClassesMap.has(classKey(c.name, c.year)) && !renamedFromKeys.has(classKey(c.name, c.year)))

        setClassComparison({ added, removed, renamed, unchanged })
    }

    const toggleSection = (section) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }))
    }

    const handleCategoryClick = (category) => {
        setActiveTab('details')
        setExpandedSections((prev) => ({
            ...prev,
            ...Object.keys(prev).reduce((acc, key) => {
                acc[key] = false
                return acc
            }, {}),
            [category]: true,
        }))
    }

    // Original functionality...
    if (!studentsData || studentsData.length === 0 || !studentAnalysis) {
        return null
    }

    const { categorized, summary, issues, columns } = studentAnalysis

    const criticalIssues = issues.filter((issue) => issue.type === 'error')
    const warningIssues = issues.filter((issue) => issue.type === 'warning')

    const getStudentName = (student) => {
        if (columns.firstNameColumn && columns.lastNameColumn && student[columns.firstNameColumn] && student[columns.lastNameColumn]) {
            return `${student[columns.firstNameColumn]} ${student[columns.lastNameColumn]}`
        }

        if (columns.fullNameColumn && student[columns.fullNameColumn]) {
            return student[columns.fullNameColumn]
        }

        for (const key of Object.keys(student)) {
            if (key.toLowerCase().includes('name') && student[key]) {
                return student[key]
            }
        }

        return 'Unknown Student'
    }

    const renderClassChanges = () => {
        const totalChanges = classComparison.added.length + classComparison.removed.length + classComparison.renamed.length

        return (
            <div className="mt-6">
                <div
                    className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#ECEBF8] px-3 rounded-tr-[12px] transition ${
                        expandedSections.classChanges ? 'rounded-br-none' : 'rounded-br-[12px]'
                    } border-l-[5px] border-deepPurple`}
                    onClick={() => toggleSection('classChanges')}
                >
                    <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                        {t('Class Changes')} ({totalChanges})
                    </h4>
                    <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                        <svg
                            className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.classChanges ? 'rotate-180' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>

                {expandedSections.classChanges && (
                    <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ">
                            <div className="bg-[#ECEBF8] p-3 rounded-lg">
                                <h5 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('New Classes')}</h5>
                                <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{classComparison.added.length}</p>
                            </div>

                            <div className="bg-[#ECEBF8] p-3 rounded-lg">
                                <h5 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Renamed Classes')}</h5>
                                <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{classComparison.renamed.length}</p>
                            </div>

                            <div className="bg-[#ECEBF8] p-3 rounded-lg">
                                <h5 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Removed Classes')}</h5>
                                <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{classComparison.removed.length}</p>
                            </div>
                        </div>

                        {classComparison.added.length > 0 && (
                            <div className="mt-4">
                                <h5 className="text-md font-medium text-darkPurple mb-2">{t('New Classes to be Added')}:</h5>
                                <div className="bg-white rounded-lg border border-[#ECEBF8] overflow-hidden">
                                    <table className="min-w-full divide-y divide-[#ECEBF8]">
                                        <thead className="bg-containerGray">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('Name')}</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('Year')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-[#ECEBF8]">
                                            {classComparison.added.map((cls, index) => (
                                                <tr key={index} className="hover:bg-green-50">
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-darkPurple">{cls.name}</td>
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">{cls.year}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {classComparison.renamed.length > 0 && (
                            <div className="mt-4">
                                <h5 className="text-md font-medium text-darkPurple mb-2">{t('Existing classes to be renamed')}:</h5>
                                <div className="bg-white rounded-lg border border-[#ECEBF8] overflow-hidden">
                                    <table className="min-w-full divide-y divide-[#ECEBF8]">
                                        <thead className="bg-containerGray">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('Existing Name')}</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('New Name')}</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('Year')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-[#ECEBF8]">
                                            {classComparison.renamed.map((cls, index) => (
                                                <tr key={index} className="hover:bg-yellow-50">
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">{cls.previousName}</td>
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-darkPurple">{cls.name}</td>
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">{cls.year}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {classComparison.removed.length > 0 && (
                            <div className="mt-4">
                                <h5 className="text-md font-medium text-darkPurple mb-2">{t('Classes to be Removed')}:</h5>
                                <div className="bg-white rounded-lg border border-[#ECEBF8] overflow-hidden">
                                    <table className="min-w-full divide-y divide-[#ECEBF8]">
                                        <thead className="bg-containerGray">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('Name')}</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-deepPurple uppercase tracking-wider">{t('Year')}</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-[#ECEBF8]">
                                            {classComparison.removed.map((cls, index) => (
                                                <tr key={index} className="hover:bg-red-50">
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-darkPurple">{cls.name}</td>
                                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">{cls.year}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        )
    }

    const renderStudentList = (studentList, highlightIssue = false, issueColumn = null) => {
        if (!studentList || studentList.length === 0) {
            return <p className="text-mudPurple font-medium italic">{t('No students in this category')}</p>
        }

        const showPasswords = settings?.useSheetPassword && columns.passwordColumn

        return (
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-lightBG">
                    <thead className="bg-lightBG">
                        <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('ID')}</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('Name')}</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('Class')}</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('Type')}</th>
                            {columns.emailColumn && <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('Email')}</th>}
                            {showPasswords && <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('password')}</th>}
                            {highlightIssue && <th className="px-4 py-2 text-left text-xs font-medium text-darkPurple uppercase tracking-wider">{t('Issue')}</th>}
                        </tr>
                    </thead>
                    <tbody className="bg-containerGray divide-y divide-lightBG">
                        {studentList.map((student, index) => {
                            const studentId = columns.idColumn ? student[columns.idColumn] || '' : ''
                            const studentName = getStudentName(student)
                            const studentClass = typeof student.classesName === 'string' ? student.classesName : columns.classColumn ? student[columns.classColumn] || '' : ''
                            const studentEmail = columns.emailColumn ? student[columns.emailColumn] || '' : ''
                            const studentPassword = showPasswords ? student[columns.passwordColumn] || '' : ''
                            const isPrimary = student.isPrimary || student.primary

                            return (
                                <tr key={index} className={highlightIssue ? 'bg-containerGray' : 'hover:bg-containerGray'}>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">{studentId}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">{studentName}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">{studentClass}</td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                                        <span className={`px-2 py-1 text-xs rounded-full ${isPrimary ? 'bg-white text-deepPurple' : 'bg-white text-deepPurple'}`}>
                                            {isPrimary ? t('Primary') : t('Secondary')}
                                        </span>
                                    </td>
                                    {columns.emailColumn && (
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">
                                            {studentEmail ||
                                                (isPrimary ? (
                                                    <span className="text-deepPurple text-xs italic">{t('Not required')}</span>
                                                ) : (
                                                    <span className="text-[#FC4C59] text-xs">{t('Required')}</span>
                                                ))}
                                        </td>
                                    )}
                                    {showPasswords && (
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-deepPurple">
                                            {studentPassword ? (
                                                <span className="font-mono px-2 py-1 rounded text-xs">{studentPassword}</span>
                                            ) : isPrimary ? (
                                                <span className="text-deepPurple text-xs italic">{t('Not required')}</span>
                                            ) : (
                                                <span className="text-[#FC4C59] text-xs">{t('Not set')}</span>
                                            )}
                                        </td>
                                    )}
                                    {highlightIssue && issueColumn && (
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-[#FC4C59]">
                                            {issueColumn === 'duplicate'
                                                ? student.duplicateWith !== undefined
                                                    ? t('duplicateWithRecord', { recordNumber: student.duplicateWith + 1 })
                                                    : t('duplicateRecord')
                                                : issueColumn === 'class'
                                                ? t('missingClass')
                                                : issueColumn === 'name'
                                                ? t('missingNameData')
                                                : issueColumn === 'id'
                                                ? t('missingId')
                                                : issueColumn === 'gender'
                                                ? t('invalidGender')
                                                : issueColumn === 'email'
                                                ? t('missingEmailSecondary')
                                                : issueColumn === 'password'
                                                ? t('missingPasswordSecondary')
                                                : t('dataIssue')}
                                        </td>
                                    )}
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        <div className="bg-[#F8F8FB] rounded-[16px] shadow p-10 ">
            <div className="flex gap-8 border-b border-[#BEBECE] mb-4">
                <button
                    onClick={() => setActiveTab('summary')}
                    className={`px-4 py-2 font-bold text-[20px] leading-[25px] -mb-[2.5px] transition-all duration-300 ease-in-out
            ${activeTab === 'summary' ? 'border-deepPurple border-b-[5px] text-darkPurple' : 'border-b-[5px] border-transparent text-[#9392AD] hover:text-darkPurple'}
        `}
                >
                    {t('Summary')}
                    {warningIssues.length > 0 && (
                        <span className="ml-2 text-sm text-[#FFA500] bg-[#FFF3CD] px-2 py-1 rounded-full" title={t('Number of warnings')}>
                            {warningIssues.length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('details')}
                    className={`px-4 py-2 font-bold text-[20px] leading-[25px] -mb-[2.5px] transition-all duration-300 ease-in-out
            ${activeTab === 'details' ? 'border-deepPurple border-b-[5px] text-darkPurple' : 'border-b-[5px] border-transparent text-[#9392AD] hover:text-darkPurple'}
        `}
                >
                    {t('Details')}
                    {warningIssues.length > 0 && (
                        <span className="ml-2 text-sm text-[#FFA500] bg-[#FFF3CD] px-2 py-1 rounded-full" title={t('Number of warnings')}>
                            {warningIssues.length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('preview')}
                    className={`px-4 py-2 font-bold text-[20px] leading-[25px] -mb-[2.5px] transition-all duration-300 ease-in-out
            ${activeTab === 'preview' ? 'border-deepPurple border-b-[5px] text-darkPurple' : 'border-b-[5px] border-transparent text-[#9392AD] hover:text-darkPurple'}
        `}
                >
                    {t('Data preview')}
                </button>
            </div>

            {activeTab === 'summary' && (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-[#ECEBF8] p-5 rounded-[12px] ">
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Total Students')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.total}</p>
                        </div>

                        {/* New Primary Students Card */}
                        <div className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors" onClick={() => handleCategoryClick('primaryStudents')}>
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Primary Students')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.primaryStudents}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple ">
                                {((summary.primaryStudents / summary.total) * 100).toFixed(1)}
                                {t('% of students')}
                            </p>
                        </div>

                        {/* New Secondary Students Card */}
                        <div
                            className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors"
                            onClick={() => handleCategoryClick('secondaryStudents')}
                        >
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Secondary Students')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.secondaryStudents}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple ">
                                {((summary.secondaryStudents / summary.total) * 100).toFixed(1)}
                                {t('% of students')}
                            </p>
                        </div>

                        <div className="bg-[#ECEBF8] p-5 rounded-[12px]">
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Total Classes')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.uniqueClasses}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple ">
                                {' '}
                                {summary.uniqueYears > 1 ? t('across5YearGroups', { count: summary.uniqueYears }) : t('across1YearGroup', { count: summary.uniqueYears })}{' '}
                            </p>
                        </div>

                        <div className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors" onClick={() => handleCategoryClick('validRecords')}>
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Valid Entries')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.validRecords}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple ">
                                {((summary.validRecords / summary.total) * 100).toFixed(1)}
                                {t('% of students')}
                            </p>
                        </div>

                        <div className={`bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors `} onClick={() => handleCategoryClick('missingClass')}>
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Missing Class')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.missingClass}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple ">
                                {((summary.missingClass / summary.total) * 100).toFixed(1)}
                                {t('% of students')}
                            </p>
                        </div>

                        <div className={`bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors `} onClick={() => handleCategoryClick('duplicateIds')}>
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Duplicate IDs')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.duplicateIds}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple ">
                                {((summary.duplicateIds / summary.total) * 100).toFixed(1)}
                                {t('% of students')}
                            </p>
                        </div>

                        <div
                            className={`bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors `}
                            onClick={() => handleCategoryClick('duplicateEmails')}
                        >
                            <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Duplicate Emails')}</h4>
                            <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.duplicateEmails}</p>
                            <p className="text-[16px] leading-[18px] font-medium text-deepPurple ">
                                {((summary.duplicateEmails / summary.total) * 100).toFixed(1)}
                                {t('% of students')}
                            </p>
                        </div>

                        {/* Missing Emails Card - only show for secondary students */}
                        {summary.missingEmails > 0 && (
                            <div
                                className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors"
                                onClick={() => handleCategoryClick('missingEmails')}
                            >
                                <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Missing Emails')}</h4>
                                <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.missingEmails}</p>
                                <p className="text-[16px] leading-[18px] font-medium text-deepPurple ">{t('Secondary students without email')}</p>
                            </div>
                        )}

                        {/* Update Missing Passwords Card - make it clear it's just a warning */}
                        {columns.passwordColumn && settings?.useSheetPassword && summary.missingPasswords > 0 && (
                            <div
                                className="bg-[#ECEBF8] p-5 rounded-[12px] cursor-pointer hover:bg-[#DEDCFB] transition-colors"
                                onClick={() => handleCategoryClick('missingPasswords')}
                            >
                                <h4 className="text-[24px] leading-[30px] font-medium text-darkPurple">{t('Missing Passwords (Warning)')}</h4>
                                <p className="text-[36px] leading-[45px] font-bold text-darkPurple mt-3">{summary.missingPasswords}</p>
                                <p className="text-[16px] leading-[18px] font-medium text-deepPurple ">{t('Will be auto-generated')}</p>
                            </div>
                        )}
                    </div>

                    {currentClasses.length > 0 && renderClassChanges()}

                    {criticalIssues.length > 0 && (
                        <div className="mt-6">
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FC4C5933] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.issues ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FC4C59]`}
                                onClick={() => toggleSection('issues')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Data Issues')} ({criticalIssues.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.issues ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.issues && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-lg p-4 bg-white">
                                    <ul className="list-disc pl-5 space-y-1 text-sm text-darkPurple">
                                        {criticalIssues.map((issue, index) => (
                                            <li key={index}>{issue.message}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                    {warningIssues.length > 0 && (
                        <div className="mt-6">
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FDDF6D66] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.warnings ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FDDF6D]`}
                                onClick={() => toggleSection('warnings')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Warnings')} ({warningIssues.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.warnings ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.warnings && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-lg p-4 bg-white">
                                    <ul className="list-disc pl-5 space-y-1 text-sm text-darkPurple">
                                        {warningIssues.map((issue, index) => (
                                            <li key={index}>{issue.message}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'details' && (
                <div className="space-y-5 mt-6">
                    {/* Primary Students Section */}
                    <div>
                        <div
                            className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#ECEBF8] px-3 rounded-tr-[12px] transition ${
                                expandedSections.primaryStudents ? 'rounded-br-none' : 'rounded-br-[12px]'
                            } border-l-[5px] border-deepPurple`}
                            onClick={() => toggleSection('primaryStudents')}
                        >
                            <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                {t('Primary Students')} ({categorized.primaryStudents.length})
                            </h4>
                            <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                <svg
                                    className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.primaryStudents ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                        {expandedSections.primaryStudents && (
                            <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStudentList(categorized.primaryStudents)}</div>
                        )}
                    </div>

                    {/* Secondary Students Section */}
                    <div>
                        <div
                            className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#ECEBF8] px-3 rounded-tr-[12px] transition ${
                                expandedSections.secondaryStudents ? 'rounded-br-none' : 'rounded-br-[12px]'
                            } border-l-[5px] border-deepPurple`}
                            onClick={() => toggleSection('secondaryStudents')}
                        >
                            <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                {t('Secondary Students')} ({categorized.secondaryStudents.length})
                            </h4>
                            <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                <svg
                                    className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.secondaryStudents ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                        {expandedSections.secondaryStudents && (
                            <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStudentList(categorized.secondaryStudents)}</div>
                        )}
                    </div>
                    {/* Missing Class Section */}
                    {/* {categorized.missingClass.length > 0 && (
                        <div>
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FC4C5933] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.missingClass ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FC4C59]`}
                                onClick={() => toggleSection('missingClass')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Missing Class Assignment')} ({categorized.missingClass.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.missingClass ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.missingClass && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStudentList(categorized.missingClass, true, 'class')}</div>
                            )}
                        </div>
                    )} */}

                    {/* Duplicate IDs Section */}
                    {categorized.duplicateIds.length > 0 && (
                        <div>
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FC4C5933] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.duplicateIds ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FC4C59]`}
                                onClick={() => toggleSection('duplicateIds')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Duplicate Student IDs')} ({categorized.duplicateIds.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.duplicateIds ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.duplicateIds && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">
                                    {renderStudentList(categorized.duplicateIds, true, 'duplicate')}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Duplicate Emails Section */}
                    {categorized.duplicateEmails.length > 0 && (
                        <div>
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FC4C5933] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.duplicateEmails ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FC4C59]`}
                                onClick={() => toggleSection('duplicateEmails')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Duplicate Email Addresses')} ({categorized.duplicateEmails.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.duplicateEmails ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.duplicateEmails && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">
                                    {renderStudentList(categorized.duplicateEmails, true, 'duplicate')}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Missing Names Section */}
                    {categorized.missingNames.length > 0 && (
                        <div>
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FDDF6D66] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.missingNames ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FDDF6D]`}
                                onClick={() => toggleSection('missingNames')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Missing Student Names')} ({categorized.missingNames.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.missingNames ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.missingNames && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStudentList(categorized.missingNames, true, 'name')}</div>
                            )}
                        </div>
                    )}

                    {/* Missing IDs Section */}
                    {categorized.missingIds.length > 0 && (
                        <div>
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FDDF6D66] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.missingIds ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FDDF6D]`}
                                onClick={() => toggleSection('missingIds')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Missing Student IDs')} ({categorized.missingIds.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.missingIds ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.missingIds && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStudentList(categorized.missingIds, true, 'id')}</div>
                            )}
                        </div>
                    )}

                    {/* Invalid Gender Section */}
                    {categorized.invalidGender.length > 0 && (
                        <div>
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FDDF6D66] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.invalidGender ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FDDF6D]`}
                                onClick={() => toggleSection('invalidGender')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Invalid Gender Data')} ({categorized.invalidGender.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.invalidGender ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.invalidGender && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">
                                    {renderStudentList(categorized.invalidGender, true, 'gender')}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Valid Entries Section */}
                    <div>
                        <div
                            className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#B7E6C380] px-3 rounded-tr-[12px] transition ${
                                expandedSections.validRecords ? 'rounded-br-none' : 'rounded-br-[12px]'
                            } border-l-[5px] border-[#2F8946]`}
                            onClick={() => toggleSection('validRecords')}
                        >
                            <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                {t('Valid Student Records')} ({categorized.validRecords.length})
                            </h4>
                            <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                <svg
                                    className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.validRecords ? 'rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                        {expandedSections.validRecords && (
                            <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">{renderStudentList(categorized.validRecords)}</div>
                        )}
                    </div>

                    {categorized.missingEmails.length > 0 && (
                        <div>
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FC4C5933] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.missingEmails ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FC4C59]`}
                                onClick={() => toggleSection('missingEmails')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Missing Email Addresses')} ({categorized.missingEmails.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.missingEmails ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.missingEmails && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">
                                    {renderStudentList(categorized.missingEmails, true, 'email')}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Missing Passwords Section - change from red to orange */}
                    {columns.passwordColumn && settings?.useSheetPassword && categorized.missingPasswords.length > 0 && (
                        <div>
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FDDF6D66] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.missingPasswords ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FDDF6D]`}
                                onClick={() => toggleSection('missingPasswords')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Missing Passwords')} ({categorized.missingPasswords.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-orange-500 transform ${expandedSections.missingPasswords ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.missingPasswords && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-[12px] p-4 bg-white">
                                    {renderStudentList(categorized.missingPasswords, true, 'password')}
                                </div>
                            )}
                        </div>
                    )}

                    {warningIssues.length > 0 && (
                        <div className="mt-6">
                            <div
                                className={`flex justify-between items-center h-[60px] cursor-pointer bg-[#FDDF6D66] px-3 rounded-tr-[12px] transition ${
                                    expandedSections.warnings ? 'rounded-br-none' : 'rounded-br-[12px]'
                                } border-l-[5px] border-[#FDDF6D]`}
                                onClick={() => toggleSection('warnings')}
                            >
                                <h4 className="text-[20px] leading-[25px] font-bold text-darkPurple">
                                    {t('Warnings')} ({warningIssues.length})
                                </h4>
                                <div className=" w-10 h-10 rounded-full bg-white flex items-center justify-center">
                                    <svg
                                        className={`h-5 w-5 text-[#BEBECE] transform transition ${expandedSections.warnings ? 'rotate-180' : ''}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedSections.warnings && (
                                <div className="border border-[#ECEBF8] border-t-0 rounded-b-lg p-4 bg-white">
                                    <ul className="list-disc pl-5 space-y-1 text-sm text-darkPurple">
                                        {warningIssues.map((issue, index) => (
                                            <li key={index}>{issue.message}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {currentClasses.length > 0 && renderClassChanges()}
                </div>
            )}

            {activeTab === 'preview' && studentsData.length > 0 && <DataViewer data={studentsData} title="Students Data" className="mt-4" hideTitle />}

            <div className="mt-8 ">
                <h4 className="text-[16px] leading-5  font-bold text-darkPurple ">{t('Student Data Requirements')}</h4>
                <ul className="text-[16px] leading-[18px] font-medium text-deepPurple space-y-3 mt-3 ml-2">
                    <li>
                        • <span className="font-medium">{t('Student ID')}:</span> {t('Required for all students')}
                    </li>
                    <li>
                        • <span className="font-medium">{t('Full Name')}:</span> {t('Required for all students')}
                    </li>
                    <li>
                        • <span className="font-medium">{t('Class Assignment')}:</span> {t('Required for all students')}
                    </li>
                    <li>
                        • <span className="font-medium">{t('Student Type')}:</span>
                        {columns.earlyAccessColumn ? (
                            <span>
                                {' '}
                                {t('Student Type: Default is Secondary. Mark as Primary only if explicitly required in the “Upstrive Early Access” column.', {
                                    columnName: columns.earlyAccessColumn,
                                })}
                            </span>
                        ) : (
                            <span> {t('All students treated as secondary (no early access column found)')}</span>
                        )}
                    </li>
                    <li>
                        • <span className="font-medium">{t('Email Address')}:</span>
                        {columns.earlyAccessColumn ? (
                            <>
                                <span> {t('Primary students: Optional')} </span> | <span>{t('Secondary students: Required and must be unique')} </span>
                            </>
                        ) : (
                            <span> {t('Required for all students (treated as secondary)')}</span>
                        )}
                    </li>
                    <li>
                        • <span className="font-medium">{t('gender')}:</span> {t('Optional, but use standard values when provided')}
                    </li>
                    {columns.passwordColumn && (
                        <li>
                            • <span className="font-medium">{t('password')}:</span>
                            {columns.earlyAccessColumn ? (
                                <>
                                    <span> {t('Primary students: Not required')} </span> |
                                    <span>
                                        {' '}
                                        {t('Secondary students')}: {settings?.useSheetPassword ? t('Used from Excel when available') : t('System-generated')}
                                    </span>
                                </>
                            ) : (
                                <span> {settings?.useSheetPassword ? t('Used from Excel when available for all students') : t('System-generated for all students')} </span>
                            )}
                        </li>
                    )}
                </ul>
                {!columns.passwordColumn && (
                    <div className="bg-[#ECEBF8] border border-[#BEBECE] rounded-[24px] p-3 flex-1 mt-[32px] ">
                        <div className="flex items-center space-x-3">
                            <div className={`bg-[#DEDCFB] w-[50px] h-[50px] rounded-full flex items-center justify-center`}>
                                <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clip-path="url(#clip0_205_4169)">
                                        <path
                                            d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                            stroke="#585695"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_205_4169">
                                            <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                            <div className="space-y-3">
                                <p className="text-[16px] leading-5 font-bold text-darkPurple">{t('Note')}</p>
                                <p className="text-[16px] leading-[18px] font-medium text-deepPurple">
                                    {t('No password column found. All students will receive system-generated passwords.')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                {!columns.earlyAccessColumn && (
                    <div className="bg-[#ECEBF8] border border-[#BEBECE] rounded-[24px] p-3 flex-1 mt-4 ">
                        <div className="flex items-center space-x-3">
                            <div className={`bg-[#DEDCFB] w-[50px] h-[50px] rounded-full flex items-center justify-center`}>
                                <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g clip-path="url(#clip0_205_4169)">
                                        <path
                                            d="M1.5 3.99959V9.99959M1.5 9.99959H7.5M1.5 9.99959L6.14 5.63959C7.21475 4.5643 8.54437 3.77879 10.0048 3.35636C11.4652 2.93393 13.0089 2.88834 14.4917 3.22385C15.9745 3.55935 17.3482 4.26502 18.4845 5.27501C19.6209 6.28499 20.4828 7.56637 20.99 8.99959M23.5 19.9996V13.9996M23.5 13.9996H17.5M23.5 13.9996L18.86 18.3596C17.7853 19.4349 16.4556 20.2204 14.9952 20.6428C13.5348 21.0652 11.9911 21.1108 10.5083 20.7753C9.02547 20.4398 7.6518 19.7342 6.51547 18.7242C5.37913 17.7142 4.51717 16.4328 4.01 14.9996"
                                            stroke="#585695"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_205_4169">
                                            <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </div>
                            <div className="space-y-3">
                                <p className="text-[16px] leading-5 font-bold text-darkPurple">{t('Info')}</p>
                                <p className="text-[16px] leading-[18px] font-medium text-deepPurple">
                                    {t('No "Upstrive Early access" column found. All students will be treated as secondary students.')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default StudentAnalysis
